import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { RV_GROUP_CLIENT_PANEL_BASE_URL } from '@/config';
import UserContext from '@/contexts/UserContext';
import getDivergencesPanelLink from '@/helpers/get-divergences-panel-link';
import dashboardIcon from '@/images/icons/dashboard-gold.svg';
import faqImage from '@/images/icons/header-faq-blue.svg';
import propertyIcon from '@/images/icons/property-gold.svg';
import settingsIcon from '@/images/icons/settings-gold.svg';

import InfoDashboardModal from '../InfoDashboardModal/InfoDashboardModal';
import InfoPropertiesModal from '../InfoPropertiesModal/InfoPropertiesModal';
import InfoRepairsModal from '../InfoRepairsModal/InfoRepairsModal';

const STYLES = {
  header: 'flex flex-col shrink-0 w-full relative bg-[#181d27] !font-["Roboto",sans-serif] shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.2),0px_4px_5px_0px_rgba(0,0,0,0.14),0px_1px_10px_0px_rgba(0,0,0,0.12)]',
  navContainer: 'flex flex-wrap justify-center w-full',
  innerContainer: 'flex justify-between items-center w-full max-w-[83.333%] basis-[83.333%]',
  buttonGroup: 'flex items-center gap-[5px]',
  divider: 'py-[12px] mx-[22px] self-stretch flex items-center',
  dividerSpan: 'border-l-2 border-[#353D4D] block h-[20px]',
  badge: 'absolute top-[2px] right-[2px] flex items-center justify-center w-[20px] h-[20px] rounded-full bg-[#9e3c5b] text-white text-[11px] leading-none pointer-events-none z-[2]',
  button: {
    base: [
      'inline-flex items-center justify-center relative',
      'min-w-[150px] min-h-[36px] py-[6px] px-4 my-[6px] mx-[4px]',
      'normal-case !text-[0.875rem] leading-[1.4em] tracking-[0.02857em]',
      'whitespace-nowrap box-border select-none align-middle',
      '!rounded-[4px] border-0 outline-none cursor-pointer',
      "!font-['Roboto',Helvetica,Arial,sans-serif] transition-all",
      'duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
    ].join(' '),
    default: 'bg-transparent text-white font-medium hover:bg-[#272E3D] disabled:!text-gray-500 disabled:cursor-not-allowed',
    active: 'bg-[#00BBDD] text-[#2B313C] font-bold pointer-events-none',
  },
};

const ClientMenu = () => {
  const me = useContext(UserContext);
  const [disabledByModality, setDisabledByModality] = useState(false);
  const [disabledByFranchisee, setDisabledByFranchisee] = useState(false);
  const [isPayPerUseModality, setIsPayPerUseModality] = useState(false);
  const [openDashboardModal, setOpenDashboardModal] = useState(false);
  const [openRepairsModal, setOpenRepairsModal] = useState(false);
  const [openPropertiesModal, setOpenPropertiesModal] = useState(false);
  const divergenceCount = useSelector(state => state.divergences.count);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    if (me) {
      setDisabledByModality(me.disabledByModality);
      setIsPayPerUseModality(me.modality === 'PAY_PER_USE');
      setDisabledByFranchisee(!me.profile?.is_franchisee_active);
    }
  }, [me]);

  const handleRedirectToApp = useCallback(url => {
    const accessToken = sessionStorage.getItem('accessToken');
    const tokenType = sessionStorage.getItem('tokenType');
    window.open(
      `${RV_GROUP_CLIENT_PANEL_BASE_URL}/${url}/#access_token=${accessToken}&token_type=${tokenType}&expires_in=&path=${url}`,
      '_blank'
    );
  }, []);

  const btnClass = useCallback(
    (url, startsWith = false) => {
      const isActive = startsWith ? pathname.startsWith(url) : pathname === url;
      return `${STYLES.button.base} ${isActive ? STYLES.button.active : STYLES.button.default}`;
    },
    [pathname]
  );

  return (
    <header className={STYLES.header}>
      <div className={STYLES.navContainer}>
        <div className={STYLES.innerContainer}>
          <div className={STYLES.buttonGroup}>
            <button
              type="button"
              className={btnClass('/orders/new', true)}
              onClick={() => navigate('/orders/new')}
              disabled={disabledByFranchisee}
            >
              <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] mr-[3px] fill-current">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              <span className="font-bold">
                {intl.formatMessage({ id: 'NEW_ORDER' })}
              </span>
            </button>

            <button type="button" className={btnClass('/orders')} onClick={() => navigate('/orders')}>
              {intl.formatMessage({ id: 'MY_INSPECTIONS' })}
            </button>

            <button
              type="button"
              className={btnClass('/divergencias')}
              onClick={getDivergencesPanelLink}
              disabled={isPayPerUseModality}
            >
              <span className="flex items-center justify-center">
                {intl.formatMessage({ id: 'DIVERGENCES' })}
                {!!divergenceCount && (
                  <span className={STYLES.badge}>
                    {divergenceCount}
                  </span>
                )}
              </span>
            </button>

            <button
              type="button"
              className={btnClass('/orders/financial-extract')}
              onClick={() => navigate('/orders/financial-extract')}
              disabled={isPayPerUseModality}
            >
              {intl.formatMessage({ id: 'EXTRACT' })}
            </button>

            <div className={STYLES.divider}>
              <span className={STYLES.dividerSpan} />
            </div>

            <button
              type="button"
              className={btnClass('/orders/dashboards')}
              onClick={() => (disabledByModality ? setOpenDashboardModal(true) : handleRedirectToApp('dashboard'))}
            >
              <img src={dashboardIcon} alt={intl.formatMessage({ id: 'DASHBOARD' })} className="mb-0.5 mr-1.5" />
              {intl.formatMessage({ id: 'DASHBOARD' })}
            </button>

            <button
              type="button"
              className={btnClass('')}
              onClick={() => (disabledByModality ? setOpenRepairsModal(true) : handleRedirectToApp('repairs'))}
            >
              <img src={settingsIcon} alt={intl.formatMessage({ id: 'ACTIONS_MENU.CHANGES' })} className="mb-0.5 mr-1.5" />
              {intl.formatMessage({ id: 'ACTIONS_MENU.CHANGES' })}
            </button>

            <button
              type="button"
              className={btnClass('')}
              onClick={() => (disabledByModality ? setOpenPropertiesModal(true) : handleRedirectToApp('properties'))}
            >
              <img src={propertyIcon} alt={intl.formatMessage({ id: 'properties' })} className="mb-0.5 mr-1.5" />
              {intl.formatMessage({ id: 'properties' })}
            </button>
          </div>

          <div className="flex items-center">
            <a href="https://gruporv.movidesk.com/kb" target="_blank" rel="noopener noreferrer">
              <img src={faqImage} alt="Faq" className="w-[25px] mr-0.5 pb-0.5" />
            </a>
          </div>

          <InfoDashboardModal
            isOpen={openDashboardModal}
            onClose={() => setOpenDashboardModal(false)}
            onConfirm={() => handleRedirectToApp('company/plan')}
          />
          <InfoRepairsModal
            isOpen={openRepairsModal}
            onClose={() => setOpenRepairsModal(false)}
            onConfirm={() => handleRedirectToApp('company/plan')}
          />
          <InfoPropertiesModal
            isOpen={openPropertiesModal}
            onClose={() => setOpenPropertiesModal(false)}
            onConfirm={() => handleRedirectToApp('company/plan')}
          />
        </div>
      </div>
    </header>
  );
};

export default ClientMenu;
